import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  registerTableMenu,
  unregisterTableMenu,
} from "src/charts/tableMenuRegistry";
import {
  exportDashboardWidgetDataApi,
  type DashboardWidgetExportFormat,
} from "src/api/dashboardApi";
import {
  downloadExportFile,
  type ReportExportResponse,
} from "src/helpers/Report";
// Reuses KpiExportMenu's existing menu chrome (.kpi-export__menu/__item) —
// same portal-dropdown-from-the-header-hamburger pattern, just with these
// two backend-declared items instead of PNG/PDF/CSV/case-list.
import "./KpiExportMenu.scss";

const MENU_WIDTH = 190;

export type WidgetDownloadOptions = {
  csv?: boolean;
  case_data?: boolean;
};

type DownloadKind = "csv" | "case_data";

type Props = {
  // The card-header hamburger (WidgetRenderer → openTableMenu) is the only
  // trigger: this menu registers under the widget id used as container id,
  // same convention as KpiExportMenu.
  menuId?: string;
  title?: string;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
  downloadOptions?: WidgetDownloadOptions;
};

export default function DownloadOptionsMenu({
  menuId,
  title,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
  downloadOptions,
}: Props) {
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const openAt = useCallback((pos: { x: number; y: number }) => {
    setMenuPos({
      x: Math.min(
        Math.max(pos.x - MENU_WIDTH, 8),
        window.innerWidth - MENU_WIDTH - 8,
      ),
      y: Math.min(pos.y + 6, window.innerHeight - 120),
    });
  }, []);

  const close = useCallback(() => setMenuPos(null), []);

  // `downloadOptions` is only passed when has_download_options is true, so its
  // presence is the gate -- the per-case item is always offered alongside
  // whatever aggregate export the backend declares.
  const hasAnyOption = !!downloadOptions;

  useEffect(() => {
    // Only claim the widget's menu slot when there's actually something to
    // show — leaves room for some other menu (e.g. a future feature) to
    // register for this widget id when has_download_options is false.
    if (!menuId || !hasAnyOption) return;
    registerTableMenu(menuId, openAt);
    return () => unregisterTableMenu(menuId);
  }, [menuId, hasAnyOption, openAt]);

  useEffect(() => {
    if (!menuPos) return;
    const onPointerDown = (e: PointerEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuPos, close]);

  const {
    mutate: download,
    isPending,
    variables: pendingKind,
  } = useMutation<ReportExportResponse, Error, DownloadKind>({
    mutationFn: (kind) => {
      if (
        !dashboardName ||
        widgetTemplateId === undefined ||
        widgetTemplateId === null
      ) {
        throw new Error("Missing widget context for download.");
      }
      // Confirmed against the real backend contract: same endpoint for both
      // buttons, distinguished entirely by fields INSIDE `filters` (not a
      // separate top-level field) — "csv" exports the widget's own aggregate
      // rows with top_n forced to "all" (a download shouldn't be limited to
      // whatever top_n happens to be showing on screen); "case_data" asks
      // for one row per case via download_level:"detail" and drops top_n
      // entirely since it doesn't apply to a per-case export.
      const filters =
        kind === "case_data"
          ? { ...(appliedFilters ?? {}), download_level: "detail" }
          : { ...(appliedFilters ?? {}), top_n: "all" };
      return exportDashboardWidgetDataApi(
        dashboardName,
        String(widgetTemplateId),
        { filters },
        "xlsx" as DashboardWidgetExportFormat,
      );
    },
    onSuccess: async (response, kind) => {
      const normalizedTitle =
        (title ?? "widget")
          .trim()
          .replace(/[\\/:*?"<>|]+/g, "_")
          .replace(/\s+/g, "_") || "widget";
      const suffix = kind === "case_data" ? "-case-data" : "";
      try {
        await downloadExportFile(
          {
            ...response,
            filename: `${normalizedTitle}${suffix}.${response.format || "xlsx"}`,
          },
          // The user's own filters, not the augmented `filters` posted to the
          // endpoint — top_n/download_level there are request mechanics.
          { appliedFilters },
        );
      } catch {
        toast.error("Failed to process download file.");
      }
      close();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to download widget data.");
      close();
    },
  });

  if (!menuPos || !hasAnyOption) return null;

  const item = (kind: DownloadKind, icon: string, label: string) => (
    <button
      type="button"
      className="kpi-export__item"
      disabled={isPending}
      onClick={() => download(kind)}
    >
      <i
        className={`bi ${isPending && pendingKind === kind ? "bi-hourglass-split" : icon}`}
        aria-hidden="true"
      />
      {label}
    </button>
  );

  return createPortal(
    <div
      ref={menuRef}
      className="kpi-export__menu"
      role="menu"
      style={{ left: menuPos.x, top: menuPos.y, width: MENU_WIDTH }}
    >
      {downloadOptions.csv &&
        item("csv", "bi-file-earmark-excel", "Download Excel Data")}
      {/* Shown whenever the backend advertises downloads at all, not only
          when download_options.case_data is set -- same rule everywhere. */}
      {item("case_data", "bi-list-ol", "Download Case Data")}
    </div>,
    document.body,
  );
}
